import React, { useState } from 'react';
import { Calendar, ArrowRight, Newspaper, Bell, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { newsItems } from '../data/mock';

const ITEMS_PER_PAGE = 6;

const NewsPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  
  // Sort news by date (latest first)
  const sortedNews = [...newsItems].sort((a, b) => {
    return new Date(b.date) - new Date(a.date);
  });
  
  const totalPages = Math.ceil(sortedNews.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentNews = sortedNews.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  
  const featuredNews = sortedNews.find(item => item.featured);
  const regularNews = currentNews.filter(item => !item.featured || currentPage > 1);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-white">
      {/* Hero Section with Banner */}
      <section 
        className="relative text-white py-40 lg:py-56 overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_senco-showcase/artifacts/c68tmxfx_News_Page_Banner.png)' }}
      >
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/50" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6">
              <Newspaper size={16} className="mr-2" />
              Stay Updated
            </div>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 drop-shadow-lg">
              News &amp; Updates
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto drop-shadow">
              Latest news, announcements, and updates from Sen &amp; Co.
            </p>
          </div>
        </div>
      </section>

      {/* News Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured News - Only on first page */}
          {currentPage === 1 && featuredNews && (
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-[#1a237e] mb-8 flex items-center">
                <span className="w-2 h-8 bg-[#c41e3a] rounded-full mr-4" />
                Featured Update
              </h2>
              <Card className="border-0 shadow-xl overflow-hidden bg-white">
                <CardContent className="p-0">
                  <div className="grid md:grid-cols-2">
                    <div className="bg-[#1a237e] p-8 md:p-12 flex items-center justify-center">
                      <div className="text-center text-white">
                        <Bell size={64} className="mx-auto mb-4 opacity-50" />
                        <p className="text-blue-200">{featuredNews.category}</p>
                      </div>
                    </div>
                    <div className="p-8 md:p-12">
                      <div className="flex items-center space-x-2 text-gray-500 text-sm mb-4">
                        <Calendar size={16} />
                        <span>{featuredNews.date}</span>
                      </div>
                      <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                        {featuredNews.title}
                      </h3>
                      <p className="text-gray-600 text-lg leading-relaxed mb-6">
                        {featuredNews.excerpt}
                      </p>
                      <Link to="/products?brand=selenide">
                        <Button className="bg-[#c41e3a] hover:bg-[#a01830] text-white">
                          View Selenide Products
                          <ArrowRight size={18} className="ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Regular News */}
          {regularNews.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-[#1a237e] mb-8 flex items-center">
                <span className="w-2 h-8 bg-[#1a237e] rounded-full mr-4" />
                {currentPage === 1 ? 'Recent Updates' : 'All Updates'}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {regularNews.map((news) => (
                  <Card key={news.id} className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                    <CardContent className="p-0">
                      <div className="h-48 bg-gray-100 flex items-center justify-center">
                        <Newspaper size={48} className="text-gray-300" />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2 text-gray-500 text-sm">
                            <Calendar size={14} />
                            <span>{news.date}</span>
                          </div>
                          {news.category && (
                            <span className="text-xs px-2 py-1 bg-[#1a237e]/10 text-[#1a237e] rounded-full">
                              {news.category}
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-[#c41e3a] transition-colors">
                          {news.title}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {news.excerpt}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex justify-center items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="border-gray-300"
              >
                <ChevronLeft size={18} />
              </Button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className={currentPage === page ? 'bg-[#1a237e] text-white' : 'border-gray-300'}
                >
                  {page}
                </Button>
              ))}
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="border-gray-300"
              >
                <ChevronRight size={18} />
              </Button>
            </div>
          )}

          {/* No News Yet */}
          {sortedNews.length === 0 && (
            <div className="text-center py-16">
              <Card className="inline-block border-0 shadow-lg bg-white">
                <CardContent className="p-8">
                  <Bell size={48} className="text-[#1a237e] mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Updates Coming Soon</h3>
                  <p className="text-gray-600">Stay tuned for the latest news from Sen &amp; Co.</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsPage;
